import React, { useState, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../services/api";
import { toast } from "react-toastify";

const CreateLiveRoomModal = ({ isOpen, onClose }) => {
  const [roomName, setRoomName] = useState("Nature Community");
  const [description, setDescription] = useState("");
  const [members, setMembers] = useState([]); // {id, username, name, ...}
  const [isPrivate, setIsPrivate] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const fileRef = useRef(null);
  const queryClient = useQueryClient();

  // All hooks must be called unconditionally (before any early return)
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ["search-contact-members", searchInput],
    queryFn: () => api.fetchConnectedContact(searchInput),
    enabled: searchInput.length > 1,
    staleTime: 1000 * 60 * 5,
  });

  const createMutation = useMutation({
    mutationFn: (formData) => api.createLiveRoom(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all_chats"] });
      toast.success("Live Room created successfully!");
      onClose();
      // Reset form
      setRoomName("Nature Community");
      setDescription("");
      setMembers([]);
      setAvatarFile(null);
      setFileName("");
      setSearchInput("");
    },
    onError: (error) => {
      console.error("Failed to create room:", error);
      toast.error("Failed to create live room. Please try again.");
    },
  });

  if (!isOpen) return null;

  const removeMember = (id) => {
    setMembers(members.filter((m) => m.id !== id));
  };

  const addMember = (user) => {
    if (!members.find((m) => m.id === user.id)) {
      setMembers([...members, user]);
    }
    setSearchInput("");
  };

  const handleCreate = () => {
    if (!roomName.trim()) {
      toast.error("Room name is required");
      return;
    }

    const formData = new FormData();
    formData.append("name", roomName);
    formData.append("description", description);
    formData.append("is_private", isPrivate ? 1 : 0);
    if (avatarFile) formData.append("avatar", avatarFile);
    formData.append("user_ids", JSON.stringify(members.map((m) => m.id)));

    createMutation.mutate(formData);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      setFileName(file.name);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-3xl w-full max-w-[644px] shadow-2xl p-6 animate-fade-in-up">
        <h2
          className="text-[22px] font-medium text-[#111827] mb-6"
          style={{ fontFamily: "Inter" }}
        >
          Create Live Room
        </h2>

        {/* Avatar Upload */}
        <label className="block mb-3 text-[17px] font-medium text-[#111827]" style={{ fontFamily: "Inter" }}>
          Avatar
        </label>
        <div
          onClick={() => fileRef.current?.click()}
          className="flex items-center justify-between px-4 py-3 border border-gray-200 rounded-xl bg-[#F9FAFB] cursor-pointer hover:bg-gray-100 transition mb-5"
        >
          <div className="flex items-center gap-2 text-gray-600">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            {/* <span className="text-[14px] text-gray-500" style={{ fontFamily: "Inter" }}>Open Folder</span> */}
            <span className="text-[14px] text-gray-700" style={{ fontFamily: "Inter" }}>
              {fileName || "No file chosen"}
            </span>
          </div>
          {fileName && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setAvatarFile(null);
                setFileName("");
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />

        {/* Room Name */}
        <label className="block mb-3 text-[17px] font-medium text-[#111827]" style={{ fontFamily: "Inter" }}>
          Live Room Name
        </label>
        <input
          value={roomName}
          onChange={(e) => setRoomName(e.target.value)}
          placeholder="Nature Community"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-[16px] font-normal outline-none focus:border-[#1F3A60] focus:ring-2 focus:ring-[#1F3A60]/20 bg-[#F9FAFB] hover:bg-white transition mb-5"
          style={{ fontFamily: "Inter" }}
        />

        {/* Description */}
        <label className="block mb-3 text-[17px] font-medium text-[#111827]" style={{ fontFamily: "Inter" }}>
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe your live room..."
          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-[16px] font-normal outline-none focus:border-[#1F3A60] focus:ring-2 focus:ring-[#1F3A60]/20 bg-[#F9FAFB] hover:bg-white transition mb-5 min-h-[80px]"
          style={{ fontFamily: "Inter" }}
        />

        {/* Members Search */}
        <label className="block mb-3 text-[17px] font-medium text-[#111827]" style={{ fontFamily: "Inter" }}>
          Add Members
        </label>

        <div className="relative mb-3">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search users by username or name..."
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-[16px] font-normal outline-none focus:border-[#1F3A60] focus:ring-2 focus:ring-[#1F3A60]/20 bg-[#F9FAFB]"
            style={{ fontFamily: "Inter" }}
          />

          {searchInput.length > 1 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
              {isSearching ? (
                <div className="p-3 text-gray-500">Searching...</div>
              ) : searchResults?.data?.length > 0 ? (
                searchResults.data.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => addMember(user)}
                    className="px-4 py-3 hover:bg-gray-100 cursor-pointer flex items-center gap-3 border-b last:border-none"
                  >
                    {/* <div className="w-8 h-8 bg-gray-200 rounded-full flex-shrink-0" /> */}
                    <div>
                      <div className="font-medium">{user.name || user.username}</div>
                      {/* <div className="text-sm text-gray-500">@{user.username}</div> */}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-3 text-gray-500">No users found</div>
              )}
            </div>
          )}
        </div>

        {/* Selected Members */}
        <div className="border border-gray-200 rounded-xl p-3 bg-[#F9FAFB] mb-6 min-h-[90px]">
          <div className="flex flex-wrap gap-2">
            {members.map((member) => (
              <span
                key={member.id}
                className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-[13px] text-gray-700"
                style={{ fontFamily: "Inter" }}
              >
                {member.name || member.username}
                <button
                  type="button"
                  onClick={() => removeMember(member.id)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </span>
            ))}
            {members.length === 0 && (
              <span className="text-gray-400 text-sm py-2">No members added yet</span>
            )}
          </div>
        </div>

        {/* Privacy Toggle */}
      {/* Privacy Toggle */}
<div className="flex items-center justify-between mb-8">
  <div>
    <h3
      className="text-[17px] font-medium text-[#111827]"
      style={{ fontFamily: "Inter" }}
    >
      Security
    </h3>
    <p
      className="text-[14px] font-normal text-[#6B7280]"
      style={{ fontFamily: "Inter" }}
    >
      Private
    </p>
  </div>

  {/* Modern Toggle Switch */}
  <button
    type="button"
    onClick={() => setIsPrivate(!isPrivate)}
    className={`relative w-[52px] h-[28px] rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#1F3A60]/30 ${
      isPrivate ? "bg-[#1F3A60]" : "bg-[#D1D5DB]"
    }`}
  >
    <span
      className={`absolute top-[2px] left-[2px] w-[24px] h-[24px] bg-white rounded-full shadow-md transition-all duration-300 ${
        isPrivate ? "translate-x-[24px]" : "translate-x-0"
      }`}
    />
  </button>
</div>

        {/* Actions */}
        <div className="flex justify-start gap-3">
          <button
            onClick={onClose}
            className="px-12 py-3 border border-[#D1D5DB] rounded-lg text-[16px] font-normal text-[#374151] hover:bg-gray-50 transition"
            style={{ fontFamily: "Inter" }}
          >
            Cancel
          </button>

          <button
            onClick={handleCreate}
            disabled={createMutation.isPending || !roomName.trim()}
            className="px-12 py-3 bg-[#1F3A60] rounded-lg text-[16px] font-normal text-white hover:bg-[#18304F] transition disabled:opacity-50"
            style={{ fontFamily: "Inter" }}
          >
            {createMutation.isPending ? "Creating..." : "Create"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up { animation: fadeInUp 0.35s ease-out; }
      `}</style>
    </div>
  );
};

export default CreateLiveRoomModal;